import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Avatar, StatusPill, CommandPalette, useCommandHotkey } from "../../components/ui";

import { API, session, whatsappSessionLabel, getInitials } from "../shared/utils";
import { Icon } from "../shared/components";
import LoginScreen from "./LoginScreen";
import GlobalTopBar from "./GlobalTopBar";
import CsatPublicPage from "./CsatPublicPage";
import ExecutiveDashboard from "../dashboard/ExecutiveDashboard";
import AttendancePage from "../whatsapp/AttendancePage";
import ContactsPage from "../contacts/ContactsPage";
import FunnelPage from "../crm/FunnelPage";
import ReportsPage from "../dashboard/ReportsPage";
import SettingsPage from "../settings/SettingsPage";
import MasterAdminPage from "../settings/MasterAdminPage";
import AutomationsPage from "../automations/AutomationsPage";
import TicketsPage from "../tickets/TicketsPage";
import SupervisorPage from "../supervisor/SupervisorPage";
import ChatbotPage from "../chatbot/ChatbotPage";
import OnboardingFlow from "../onboarding/OnboardingFlow";
import CampaignsPage from "../campaigns/CampaignsPage";
import KnowledgePage from "../knowledge/KnowledgePage";
import { ToastProvider } from "../shared/toast";
import { usePushNotifications } from "../shared/usePushNotifications";

// ── Splash screen ─────────────────────────────────────────────────────────────

function SplashScreen({ visible }) {
  return (
    <div className={`sz-splash${visible ? " sz-splash--in" : " sz-splash--out"}`}>
      <div className="sz-splash-row">
        <span className="sz-splash-dot" />
        <span className="sz-splash-text">Carregando Safe Zap...</span>
      </div>
    </div>
  );
}

// ── App root ─────────────────────────────────────────────────────────────────

function App() {
  const csatToken = new URLSearchParams(window.location.search).get("csat");

  const [token, setToken] = useState(session.getToken);
  const [user, setUser] = useState(session.getUser);
  const [splashVisible, setSplashVisible] = useState(false);
  const [splashDone, setSplashDone] = useState(true);

  // Public CSAT page — no auth required, render immediately
  if (csatToken) return <CsatPublicPage token={csatToken} />;

  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: API,
      timeout: 20000, // 20s — evita request pendurado em Railway cold start / SQLite busy
    });
    instance.interceptors.request.use((config) => {
      const currentToken = session.getToken();
      if (currentToken) config.headers.Authorization = `Bearer ${currentToken}`;
      return config;
    });
    // Interceptor de resposta — converte timeout/rede em mensagem legível
    instance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          error._friendlyMessage = "O servidor demorou para responder. Tente novamente.";
        } else if (!error.response) {
          error._friendlyMessage = "Não foi possível conectar ao servidor. Verifique sua conexão.";
        }
        return Promise.reject(error);
      }
    );
    return instance;
  }, []);

  const logout = () => {
    session.clear();
    setToken("");
    setUser(null);
  };

  // Chamado pelo LoginScreen ao autenticar com sucesso
  function handleLogin(newToken, newUser) {
    setSplashDone(false);
    setSplashVisible(true);
    // Salva credenciais mas ainda não mostra dashboard
    session.save(newToken, newUser);

    // Após 1.8s começa a fade-out da splash
    setTimeout(() => {
      setSplashVisible(false);
      // Aguarda fade-out (400ms) e então mostra o dashboard
      setTimeout(() => {
        setToken(newToken);
        setUser(newUser);
        setSplashDone(true);
      }, 420);
    }, 1800);
  }

  // Splash sobrepõe tudo enquanto está ativa
  if (!splashDone) return <SplashScreen visible={splashVisible} />;

  if (!token || !user) return <LoginScreen setToken={(t) => {}} setUser={(u) => {}} onLogin={handleLogin} />;
  return (
    <ToastProvider>
      <Dashboard api={api} user={user} logout={logout} />
    </ToastProvider>
  );
}

// ── App shell ─────────────────────────────────────────────────────────────────

function Dashboard({ api, user, logout }) {
  const { permission: pushPermission, enable: enablePush } = usePushNotifications(api);
  const [pushBannerDismissed, setPushBannerDismissed] = useState(
    () => localStorage.getItem("safezap_push_dismissed") === "1"
  );
  const showPushBanner = pushPermission === "default" && !pushBannerDismissed;

  function dismissPushBanner() {
    localStorage.setItem("safezap_push_dismissed", "1");
    setPushBannerDismissed(true);
  }
  async function handleEnablePush() {
    const result = await enablePush();
    if (result === "granted" || result === "denied") dismissPushBanner();
  }
  // Master entra direto no Painel SaaS — não tem acesso às outras telas
  const [activePage, setActivePage] = useState(user.role === "master" ? "saas" : "dashboard");
  const [unreadConversations, setUnreadConversations] = useState(0);
  const [activeConversationId, setActiveConversationId] = useState(null);
  // Conversation ID to auto-open when navigating into Atendimentos
  // (used by Live Panel "Ir para conversa" — set BEFORE switching page)
  const [pendingConversationId, setPendingConversationId] = useState(null);
  // Deep-link de sub-tabs em páginas com tabs internas (Configurações, Relatórios)
  const [pendingSettingsTab, setPendingSettingsTab] = useState(null);
  const [pendingReportsTab, setPendingReportsTab] = useState(null);
  const [toast, setToast] = useState(null);
  const [sidebarSession, setSidebarSession] = useState({ status: "disconnected", phone: null });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Sidebar: grupos expandidos (state Set)
  const [expandedItems, setExpandedItems] = useState(new Set());
  // Sidebar: sub-item ativo por grupo (Map: parentId → subId)
  const [activeSubItems, setActiveSubItems] = useState(new Map());
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [notifRefreshToken, setNotifRefreshToken] = useState(0);
  const toastTimeoutRef = useRef(null);

  useCommandHotkey(setPaletteOpen);

  // Close sidebar when page changes (mobile navigation)
  const navigateTo = (page, payload) => {
    if (page === "atendimentos" && payload?.conversationId) {
      setPendingConversationId(payload.conversationId);
    }
    if (page === "config" && payload?.tab) {
      setPendingSettingsTab(payload.tab);
      setExpandedItems((prev) => { const s = new Set(prev); s.add("config"); return s; });
      setActiveSubItems((prev) => { const m = new Map(prev); m.set("config", payload.tab); return m; });
    }
    if (page === "relatorios" && payload?.tab) {
      setPendingReportsTab(payload.tab);
      setExpandedItems((prev) => { const s = new Set(prev); s.add("relatorios"); return s; });
      setActiveSubItems((prev) => { const m = new Map(prev); m.set("relatorios", payload.tab); return m; });
    }
    setActivePage(page);
    setSidebarOpen(false);
  };

  function toggleSidebarItem(id) {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Estrutura nova: cada item é { id, label, icon, count?, sub?[] }
  // sub-items: { id, label, payload? }  payload é passado via navigateTo
  const sidebarSections = [
    {
      label: "Operação",
      items: [
        { id: "dashboard",   label: "Resumo", icon: "home" },
        { id: "atendimentos", label: "Conversas", icon: "chat", count: unreadConversations },
      ],
    },
    {
      label: "CRM",
      items: [
        { id: "contatos", label: "Contatos", icon: "contact" },
        { id: "funil",    label: "Lead",     icon: "deal" },
      ],
    },
    {
      label: "Suporte",
      items: [
        { id: "tickets",    label: "Tickets",    icon: "ticket" },
        { id: "automacoes", label: "Automações", icon: "zap" },
        { id: "chatbot",    label: "Chatbot",    icon: "ai" },
        { id: "supervisor", label: "Supervisor", icon: "activity" },
      ],
    },
    {
      label: "Marketing",
      items: [
        { id: "campanhas",    label: "Campanhas",            icon: "send" },
        { id: "conhecimento", label: "Base de conhecimento", icon: "book" },
      ],
    },
    {
      label: "Gestão",
      items: [
        {
          id: "relatorios", label: "Relatórios", icon: "report",
          sub: [
            { id: "geral",       label: "Geral",       payload: { tab: "geral" } },
            { id: "atendimento", label: "Atendimento", payload: { tab: "atendimento" } },
            { id: "funil",       label: "Funil",       payload: { tab: "funil" } },
            { id: "qualidade",   label: "Qualidade",   payload: { tab: "qualidade" } },
            { id: "csat",        label: "Avaliações",  payload: { tab: "csat" } },
          ],
        },
        {
          id: "config", label: "Configurações", icon: "settings",
          sub: [
            { id: "perfil",    label: "Perfil",             payload: { tab: "perfil"    } },
            { id: "assinatura",label: "Assinatura",          payload: { tab: "assinatura"} },
            { id: "equipe",    label: "Equipe & permissão",  payload: { tab: "equipe"    } },
            { id: "whatsapp",  label: "WhatsApp & Sessões",  payload: { tab: "whatsapp"  } },
            { id: "crm",       label: "CRM & Funil",         payload: { tab: "crm"       } },
            { id: "tags",      label: "Tags & categorias",   payload: { tab: "tags"      } },
            { id: "horarios",  label: "Horários & SLA",      payload: { tab: "horarios"  } },
            { id: "safeIA",    label: "SAFE IA",             payload: { tab: "safeIA"    } },
            { id: "webhooks",  label: "WebHooks & API",      payload: { tab: "webhooks"  } },
            { id: "plano",     label: "Plano & Faturamento", payload: { tab: "plano"     } },
            { id: "sistema",   label: "Sistema & Aparência", payload: { tab: "sistema"   } },
          ],
        },
      ],
    },
  ];

  if (user.role === "master") {
    sidebarSections.push({
      label: "Plataforma",
      items: [{ id: "saas", label: "Painel SaaS", icon: "grid" }],
    });
  }

  async function loadUnreadConversations() {
    try {
      const response = await api.get("/conversations", { params: { limit: 50 } });
      const data = response.data;
      // New paginated format returns { conversations, total_unread, has_more, next_cursor }
      if (Array.isArray(data)) {
        setUnreadConversations(data.filter((item) => Number(item.unread_count || 0) > 0).length);
      } else {
        setUnreadConversations(data.total_unread || 0);
      }
    } catch {}
  }

  async function loadSidebarSession() {
    try {
      const response = await api.get("/whatsapp/session");
      setSidebarSession(response.data || { status: "disconnected", phone: null });
    } catch {
      setSidebarSession({ status: "disconnected", phone: null });
    }
  }

  function showIncomingToast(payload) {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({
      title: payload.contactName || "Nova mensagem",
      message: payload.preview || "Mensagem recebida no WhatsApp",
      conversationId: payload.conversationId,
    });
    toastTimeoutRef.current = setTimeout(() => setToast(null), 4500);
  }

  useEffect(() => {
    loadUnreadConversations();
    loadSidebarSession();
    const interval = setInterval(() => {
      loadUnreadConversations();
      loadSidebarSession();
    }, 20000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let source = null;
    let reconnectTimeout = null;

    async function connectRealtime() {
      try {
        const tokenResponse = await api.post("/realtime/token");
        if (cancelled) return;
        source = new EventSource(`${API}/realtime/stream?token=${encodeURIComponent(tokenResponse.data.token)}`);

        const refreshUnread = () => loadUnreadConversations();
        const onMessageCreated = (event) => {
          try {
            const payload = JSON.parse(event.data || "{}");
            refreshUnread();
            if (payload.senderType === "cliente" && (activePage !== "atendimentos" || Number(activeConversationId) !== Number(payload.conversationId))) {
              showIncomingToast(payload);
            }
          } catch {}
        };

        source.addEventListener("message_created", onMessageCreated);
        source.addEventListener("conversation_updated", refreshUnread);
        source.addEventListener("summary_updated", refreshUnread);
        source.addEventListener("notification_created", () => setNotifRefreshToken((t) => t + 1));

        source.onerror = () => {
          source?.close();
          if (cancelled) return;
          reconnectTimeout = setTimeout(connectRealtime, 2500);
        };
      } catch {
        if (cancelled) return;
        reconnectTimeout = setTimeout(connectRealtime, 2500);
      }
    }

    connectRealtime();

    return () => {
      cancelled = true;
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
      source?.close();
    };
  }, [activeConversationId, activePage, api]);

  function renderNav(items) {
    return items.map((item) => {
      const it = Array.isArray(item)
        ? { id: item[0], label: item[1], icon: item[2], count: item[3] }
        : item;
      const isActive = activePage === it.id;
      const hasSub = Array.isArray(it.sub) && it.sub.length > 0;
      const isExpanded = expandedItems.has(it.id);
      return (
        <div key={it.id}>
          <button
            className={`sb-item${isActive ? " active" : ""}`}
            onClick={() => {
              if (hasSub) {
                toggleSidebarItem(it.id);
                if (!isExpanded) navigateTo(it.id); // navega só ao abrir
              } else {
                navigateTo(it.id);
              }
            }}
          >
            <Icon name={it.icon} size={15} />
            <span className="sb-item-label">{it.label}</span>
            {it.count > 0 && <span className="count count-danger">{it.count}</span>}
            {hasSub && (
              <Icon
                name="chevronDown"
                size={12}
                className={`sb-item-chevron${isExpanded ? " open" : " closed"}`}
              />
            )}
          </button>
          {hasSub && isExpanded && (
            <div className="sb-sub">
              {it.sub.map((s) => (
                <button
                  key={s.id}
                  className={`sb-sub-item${activeSubItems.get(it.id) === s.id ? " active" : ""}`}
                  onClick={() => navigateTo(it.id, s.payload || {})}
                >
                  <span className="sb-sub-dot" />
                  {s.label}
                </button>
              ))}
            </div>
          )}
        </div>
      );
    });
  }

  // No painel SaaS o operador master tem foco total — esconde sidebar
  const isMasterPanel = activePage === "saas" && user.role === "master";

  const paletteCommands = useMemo(() => {
    const cmds = [];
    sidebarSections.forEach((section) => {
      section.items.forEach((item) => {
        cmds.push({
          id: `nav:${item.id}`,
          label: item.label,
          hint: section.label || "Navegar",
          group: section.label || "Navegar",
          keywords: [item.label, item.id, section.label || ""].filter(Boolean),
          run: () => navigateTo(item.id),
        });
        (item.sub || []).forEach((sub) => {
          cmds.push({
            id: `nav:${item.id}:${sub.id}`,
            label: `${item.label} → ${sub.label}`,
            hint: `Abrir aba ${sub.label}`,
            group: section.label || "Navegar",
            keywords: [item.label, sub.label, sub.id],
            run: () => navigateTo(item.id, sub.payload),
          });
        });
      });
    });
    cmds.push({
      id: "act:logout",
      label: "Sair da conta",
      hint: "Encerra a sessão atual",
      group: "Conta",
      keywords: ["logout", "sair", "deslogar"],
      run: () => logout(),
    });
    return cmds;
  }, [user, unreadConversations]); // sidebarSections rebuilt each render; deps cover identity

  return (
    <div className={`app-shell${isMasterPanel ? " masterShell" : ""}`}>
      {sidebarOpen && !isMasterPanel && <div className="sidebarOverlay" onClick={() => setSidebarOpen(false)} />}

      {!isMasterPanel && (
      <aside className={`sidebar${sidebarOpen ? " open" : ""}`}>
        {/* Brand */}
        <div className="sb-brand">
          <div className="sb-brand-logo">
            <svg width="28" height="28" viewBox="0 0 34 34" fill="none">
              <defs>
                <linearGradient id="sb-zmono" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#3DEB7E" />
                  <stop offset="100%" stopColor="#1FB855" />
                </linearGradient>
              </defs>
              <rect x="1" y="1" width="32" height="32" rx="8" stroke="url(#sb-zmono)" strokeWidth="2" fill="none" />
              <path d="M11 9 L23 9 L13 19 L21 19 L11 25" stroke="url(#sb-zmono)" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
            </svg>
          </div>
          <div className="sb-brand-text">
            <div className="sb-brand-name">Safe<span className="sb-brand-name-zap">Zap</span></div>
            <div className="sb-brand-sub">CRM Inteligente</div>
          </div>
        </div>

        {/* Workspace — WhatsApp connection status */}
        <button className="sb-workspace" onClick={() => navigateTo("config", { tab: "whatsapp" })}>
          <Avatar name={user.company_name || "Empresa"} size="sm" />
          <div className="sb-workspace-text">
            <div className="sb-workspace-name">{user.company_name || "Minha Empresa"}</div>
            <div className="sb-workspace-sub">
              <span className="badge-dot" style={{ background: sidebarSession.status === "connected" ? "var(--success)" : sidebarSession.status === "qr_ready" ? "var(--warning)" : "var(--text-4)" }} />
              {sidebarSession.status === "connected" ? (sidebarSession.phone || "WhatsApp conectado") : whatsappSessionLabel(sidebarSession.status)}
            </div>
          </div>
          <Icon name="chevronDown" size={12} />
        </button>

        {/* Search / Command Palette */}
        <button className="sb-search" onClick={() => setPaletteOpen(true)}>
          <Icon name="search" size={13} />
          <span>Pesquisar...</span>
          <span className="kbd" style={{ marginLeft: "auto" }}>⌘K</span>
        </button>

        {/* Nav */}
        <nav className="sb-nav">
          {sidebarSections.map((section) => (
            <div className="sb-group" key={section.label || "main"}>
              {section.label && <div className="sb-group-label">{section.label}</div>}
              {renderNav(section.items)}
            </div>
          ))}
        </nav>

        {/* Sair — acima da linha divisória */}
        <button className="sb-logout" onClick={logout} title="Sair da conta">
          <Icon name="logout" size={14} />
          <span>Sair</span>
        </button>

        {/* Footer */}
        <div className="sb-footer">
          <button className="sb-user" onClick={() => navigateTo("config", { tab: "perfil" })}>
            <Avatar name={user.name} size="sm" />
            <div className="sb-user-text">
              <div className="sb-user-name">{user.name}</div>
              <div className="sb-user-role">{user.role} · v2.0</div>
            </div>
          </button>
          <button className="sb-collapse" onClick={() => setSidebarOpen(false)} title="Fechar menu">
            <Icon name="chevronLeft" size={12} />
          </button>
        </div>
      </aside>
      )}

      <main className="app-main">
        {/* ── Push notification permission banner ── */}
        {showPushBanner && (
          <div className="pushPermBanner">
            <span className="pushPermBannerIcon">🔔</span>
            <div className="pushPermBannerText">
              <strong>Ativar notificações</strong>
              <span>Receba alertas de novas mensagens mesmo com o Chrome minimizado ou em segundo plano.</span>
            </div>
            <button className="btn btn-primary btn-sm" onClick={handleEnablePush}>
              Ativar agora
            </button>
            <button className="btn btn-ghost btn-sm" onClick={dismissPushBanner} title="Fechar">
              ✕
            </button>
          </div>
        )}
        <GlobalTopBar
          api={api}
          user={user}
          activePage={activePage}
          setActivePage={navigateTo}
          onMenuToggle={() => setSidebarOpen((o) => !o)}
          onOpenPalette={() => setPaletteOpen(true)}
          unreadCount={unreadConversations}
          logout={logout}
          notifRefreshToken={notifRefreshToken}
        />
        <div className="app-content">
          {toast && (
            <button className="inboxToast" onClick={() => { navigateTo("atendimentos"); setToast(null); }}>
              <strong>{toast.title}</strong>
              <span>{toast.message}</span>
            </button>
          )}
          {activePage === "dashboard" && <ExecutiveDashboard api={api} user={user} setActivePage={navigateTo} />}
          {activePage === "atendimentos" && <AttendancePage api={api} user={user} onActiveConversationChange={setActiveConversationId} onUnreadChange={setUnreadConversations} setActivePage={navigateTo} initialConversationId={pendingConversationId} clearInitialConversation={() => setPendingConversationId(null)} />}
          {activePage === "contatos" && <ContactsPage api={api} onNavigate={navigateTo} />}
          {activePage === "funil" && <FunnelPage api={api} user={user} setActivePage={navigateTo} />}
          {activePage === "tickets" && <TicketsPage api={api} user={user} />}
          {activePage === "automacoes" && <AutomationsPage api={api} />}
          {activePage === "chatbot" && <ChatbotPage api={api} />}
          {activePage === "supervisor" && <SupervisorPage api={api} user={user} setActivePage={navigateTo} />}
          {activePage === "campanhas" && <CampaignsPage api={api} user={user} />}
          {activePage === "conhecimento" && <KnowledgePage api={api} />}
          {activePage === "relatorios" && (
            <ReportsPage
              api={api}
              initialTab={pendingReportsTab}
              clearInitialTab={() => setPendingReportsTab(null)}
            />
          )}
          {activePage === "config" && (
            <SettingsPage
              api={api}
              user={user}
              initialTab={pendingSettingsTab}
              clearInitialTab={() => setPendingSettingsTab(null)}
              onUserUpdate={(updated) => {
                const next = { ...user, ...updated };
                setUser(next);
                session.save(token, next);
              }}
            />
          )}
          {activePage === "saas" && user.role === "master" && <MasterAdminPage api={api} onLogout={logout} />}
        </div>
      </main>

      <OnboardingFlow onNavigate={navigateTo} hideTrigger />

      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        commands={paletteCommands}
      />
    </div>
  );
}

export default App;
